import { useEffect, useState } from 'react';
import { useAuth } from '../Auth';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Box, Typography, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export default function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    useEffect(() => {
        if (user) {
            if (user.requiresPasswordChange) {
                navigate('/force-password-change', { replace: true });
            } else {
                const path = (user.role === 'superuser' && !user.tenant_id)
                    ? '/admin/tenants'
                    : '/';
                navigate(path, { replace: true });
            }
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        // 1. Prevent ANY default browser action or bubbling
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setError('');
        setLoading(true);

        try {
            const result = await login(username.trim(), password);

            // 2. We only navigate if successful. 
            // The useEffect [user] is a backup, but doing it here is more stable.
            if (result && result.success) {
                // Check the flag from the result object if your login() returns it
                if (result.user?.requiresPasswordChange) {
                    navigate('/force-password-change', { replace: true });
                } else {
                    navigate(result.redirectTo, { replace: true });
                }
            } else {
                // 3. CRITICAL: Manual stop of loading and setting error
                // This keeps the component mounted and the state intact
                setLoading(false);
                const errorMsg = result?.message || 'Invalid username or password';
                setError(errorMsg);
            }
        } catch (err) {
            console.error("Login component error:", err);
            setLoading(false);
            setError('An unexpected error occurred.');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Link to your image in the public folder
                backgroundImage: `url('/AchievED_background.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <Box
                sx={{
                    maxWidth: 420,
                    width: '100%',
                    mx: 2,
                    p: 4,
                    borderRadius: 4,
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',

                    /* Frosted Glass Effect */
                    backgroundColor: 'rgba(255, 255, 255, 0.15)', // Light translucent white
                    backdropFilter: 'blur(10px)',                // The "frost" blur
                    WebkitBackdropFilter: 'blur(10px)',          // Support for Safari
                    border: '1px solid rgba(255, 255, 255, 0.18)', // Subtle highlight edge

                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* <Typography variant="h3" align="center" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
                    VeriSafe
                </Typography> */}
                <Box
                    component="img"
                    src="/Verisafe logo light.png"
                    alt="VeriSafe Logo"
                    sx={{
                        height: 120,
                        width: '100%',
                        objectFit: 'contain', // Ensures the whole logo stays visible
                        display: 'block',
                        mb: -2,               // Negative margin to pull the tagline closer
                        mt: -2                // Negative margin to pull the logo toward the top
                    }}
                />
                <Typography
                    variant="body2"
                    component="h1"
                    align="center"
                    sx={{
                        mb: 4,
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.875rem', // Keeps the small "body2" size
                        letterSpacing: '1px',
                    }}
                >
                    {/* Track · Achieve · Repeat */}
                    Secure · Verify · Trust
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.2)', color: '#ffcdd2' }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit} noValidate>
                    <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoFocus
                        disabled={loading}
                        autoComplete="off"
                        // Making inputs slightly transparent to match the theme
                        sx={{
                            // 1. Background and base text colors
                            '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.05)' },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                            '& .MuiInputBase-input': { color: 'white' },

                            // 2. Change the label color when focused
                            '& .MuiInputLabel-root.Mui-focused': { color: '#222222' },

                            // 3. Change the outline color when focused
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#222222',
                            }
                        }}
                    />
                    <TextField
                        label="Password"
                        type={showPassword ? 'text' : 'password'} // Dynamic type
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        autoComplete="off"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.05)' },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                            '& .MuiInputBase-input': { color: 'white' },

                            // Focus Styles
                            '& .MuiInputLabel-root.Mui-focused': { color: '#222222' },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#222222',
                            }
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{
                            mt: 4,
                            py: 1.5,
                            // Dark grey background
                            bgcolor: '#333333',
                            color: 'white',
                            fontWeight: 'bold',
                            textTransform: 'none', // Keeps it "Sign In" instead of "SIGN IN"
                            borderRadius: 2,
                            '&:hover': {
                                bgcolor: '#000000', // Turns pure black on hover
                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                color: 'rgba(255, 255, 255, 0.3)'
                            }
                        }}
                        fullWidth
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                    </Button>
                </form>
            </Box>
        </Box>
    );
}